import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { notificationsService } from '../services/notificationsService'
import type { Notification, NotificationFilters, NotificationCounts } from '../types/notifications'
import { NotificationStatus } from '../types/notifications'

export interface UseNotificationsReturn {
  // State
  notifications: Notification[]
  unreadCount: number
  totalCount: number
  isLoading: boolean
  error: string | null
  
  // Actions
  markAsRead: (notificationId: string) => Promise<void>
  markMultipleAsRead: (notificationIds: string[]) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (notificationId: string) => Promise<void>
  deleteMultipleNotifications: (notificationIds: string[]) => Promise<void>
  deleteAllNotifications: () => Promise<void>
  refreshNotifications: () => Promise<void>
  
  // Filters
  filters: NotificationFilters
  setFilters: (filters: NotificationFilters) => void
  clearFilters: () => void
  
  // Counts
  counts: NotificationCounts | null
  refreshCounts: () => Promise<void>
}

export const useNotifications = (
  profileId?: string,
  initialFilters: NotificationFilters = {}
): UseNotificationsReturn => {
  const { currentUser, isAuthenticated, loading: authLoading, setupError } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [counts, setCounts] = useState<NotificationCounts | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<NotificationFilters>({
    ...initialFilters,
    profileId
  })
  
  const unsubscribeRef = useRef<(() => void) | null>(null)

  // Refresh notifications
  const refreshNotifications = useCallback(async () => {
    if (!currentUser?.uid || authLoading || !isAuthenticated || setupError) return

    setIsLoading(true)
    setError(null)
    
    try {
      const fetchedNotifications = await notificationsService.getUserNotifications(
        currentUser.uid,
        filters
      )
      setNotifications(fetchedNotifications)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load notifications'
      setError(errorMessage)
      console.error('Error refreshing notifications:', err)
    } finally {
      setIsLoading(false)
    }
  }, [currentUser?.uid, filters, authLoading, isAuthenticated, setupError])

  // Refresh counts
  const refreshCounts = useCallback(async () => {
    if (!currentUser?.uid || authLoading || !isAuthenticated || setupError) return

    try {
      const fetchedCounts = await notificationsService.getNotificationCounts(currentUser.uid)
      setCounts(fetchedCounts)
    } catch (err) {
      console.error('Error refreshing notification counts:', err)
    }
  }, [currentUser?.uid, authLoading, isAuthenticated, setupError])

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const target = notifications.find(n => n.id === notificationId)
      if (!target) {
        console.warn('Cannot mark notification as read: notification not found', notificationId)
        throw new Error('Cannot mark notification as read: notification not found')
      }

      await notificationsService.markAsRead(
        notificationId,
        target.profileId || '',
        target.isAccountLevel,
        target.documentPath
      )
      
      // Update local state
      setNotifications(prev => prev.map(n => (n.id === notificationId ? { ...n, readAt: new Date(), status: NotificationStatus.READ } : n)))
      
      // Refresh counts
      await refreshCounts()
    } catch (err) {
      console.error('Error marking notification as read:', err)
      throw err
    }
  }, [notifications, refreshCounts])

  // Mark multiple notifications as read
  const markMultipleAsRead = useCallback(async (notificationIds: string[]) => {
    try {
      const refs = notificationIds
        .map(id => notifications.find(n => n.id === id))
        .filter((n): n is Notification => Boolean(n))
        .map(n => ({ 
          id: n.id, 
          profileId: n.profileId || '', 
          isAccountLevel: n.isAccountLevel,
          documentPath: n.documentPath
        }))

      if (refs.length === 0) {
        return
      }

      await notificationsService.markMultipleAsRead(refs)
      
      // Update local state
      setNotifications(prev => prev.map(n => (notificationIds.includes(n.id) ? { ...n, readAt: new Date(), status: NotificationStatus.READ } : n)))
      
      // Refresh counts
      await refreshCounts()
    } catch (err) {
      console.error('Error marking multiple notifications as read:', err)
      throw err
    }
  }, [notifications, refreshCounts])

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsService.markAllAsRead(currentUser!.uid)
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, readAt: new Date(), status: NotificationStatus.READ })))
      
      // Refresh counts
      await refreshCounts()
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
      throw err
    }
  }, [currentUser?.uid, refreshCounts])

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const target = notifications.find(n => n.id === notificationId)
      if (!target) {
        console.warn('Cannot delete notification: notification not found', notificationId)
        throw new Error('Cannot delete notification: notification not found')
      }

      await notificationsService.deleteNotification(
        notificationId,
        target.profileId || '',
        target.isAccountLevel,
        target.documentPath
      )
      
      // Update local state
      setNotifications(prev => prev.filter(n => n.documentPath !== target.documentPath))
      
      // Refresh counts
      await refreshCounts()
    } catch (err) {
      console.error('Error deleting notification:', err)
      throw err
    }
  }, [notifications, refreshCounts])

  // Delete multiple notifications
  const deleteMultipleNotifications = useCallback(async (notificationIds: string[]) => {
    try {
      const refs = notificationIds
        .map(id => notifications.find(n => n.id === id))
        .filter((n): n is Notification => Boolean(n))
        .map(n => ({ 
          id: n.id, 
          profileId: n.profileId || '', 
          isAccountLevel: n.isAccountLevel,
          documentPath: n.documentPath
        }))

      if (refs.length === 0) {
        return
      }

      await notificationsService.deleteMultipleNotifications(refs)
      
      // Update local state
      setNotifications(prev => prev.filter(n => !refs.some(ref => ref.documentPath === n.documentPath)))
      
      // Refresh counts
      await refreshCounts()
    } catch (err) {
      console.error('Error deleting multiple notifications:', err)
      throw err
    }
  }, [notifications, refreshCounts])

  // Delete all notifications
  const deleteAllNotifications = useCallback(async () => {
    try {
      await notificationsService.deleteAllNotifications(currentUser!.uid)
      
      // Update local state
      setNotifications([])
      
      // Refresh counts
      await refreshCounts()
    } catch (err) {
      console.error('Error deleting all notifications:', err)
      throw err
    }
  }, [currentUser?.uid, refreshCounts])

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({ profileId })
  }, [profileId])

  // Set up real-time subscription
  useEffect(() => {
    if (!currentUser?.uid || authLoading || !isAuthenticated || setupError) return

    // Clean up previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current()
    }

    // Set up new subscription
    unsubscribeRef.current = notificationsService.subscribeToNotifications(
      currentUser.uid,
      (updatedNotifications) => {
        setNotifications(updatedNotifications)
      },
      filters
    )

    // Initial load
    refreshNotifications()
    refreshCounts()

    // Cleanup on unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
      }
    }
  }, [currentUser?.uid, filters, refreshNotifications, refreshCounts, authLoading, isAuthenticated, setupError])

  // Update filters when profileId changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, profileId }))
  }, [profileId])

  // Computed values
  const unreadCount = counts?.unread || 0
  const totalCount = counts?.total || 0

  return {
    // State
    notifications,
    unreadCount,
    totalCount,
    isLoading,
    error,
    
    // Actions
    markAsRead,
    markMultipleAsRead,
    markAllAsRead,
    deleteNotification,
    deleteMultipleNotifications,
    deleteAllNotifications,
    refreshNotifications,
    
    // Filters
    filters,
    setFilters,
    clearFilters,
    
    // Counts
    counts,
    refreshCounts
  }
}
